import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import TextField from "@/common/components/TextField";
import { InfoIcon } from "lucide-react";
import { twMerge } from "tailwind-merge";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { FormTransformers } from "@/common/utils/formTransformers";
import useBlockClientCompany from "@/features/privateCompanies/hooks/useBlockClientCompany";
import useUnblockClientCompany from "@/features/privateCompanies/hooks/useUnblockClientCompany";
import { CompanyData } from "@/features/companies/types/companies.type";
import { companyBlockUnblockSchema, CompanyBlockUnblockValidationSchema } from "@/features/privateCompanies/schemas/blockUnblockCompany.schema";

const BlockUnblockCompany = ({ company }: { company?: CompanyData; }) => {
  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const [blockModalIsOpen, setBlockModalIsOpen] = useState(false);
  const [unblockModalIsOpen, setUnblockModalIsOpen] = useState(false);

  const { mutate: blockCompany } = useBlockClientCompany({
    companyId: company?.id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', company?.id] });
      blockFormReset();
      setBlockModalIsOpen(false);
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError: blockFormSetError })(error);
      setMessages((error.response?.data as { errors: { base: string[]; }; })?.errors?.base || null);
    },
  });

  const { mutate: unblockCompany } = useUnblockClientCompany({
    companyId: company?.id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['company', company?.id] });
      unblockFormReset();
      setUnblockModalIsOpen(false);
    },
    onError: (error) => {
      FormTransformers.errorsTransformer({ setError: unblockFormSetError })(error);
      setMessages((error.response?.data as { errors: { base: string[]; }; })?.errors?.base || null);
    },
  });

  const isBlocked = company?.status === 'blocked';
  const isDisabled = company?.status === 'disabled';

  const {
    register: blockFormRegister,
    reset: blockFormReset,
    setError: blockFormSetError,
    handleSubmit: blockFormHandleSubmit,
    formState: { errors: blockFormErrors },
  } = useForm<CompanyBlockUnblockValidationSchema>({
    resolver: zodResolver(companyBlockUnblockSchema),
  });

  const {
    register: unblockFormRegister,
    reset: unblockFormReset,
    setError: unblockFormSetError,
    handleSubmit: unblockFormHandleSubmit,
    formState: { errors: unblockFormErrors },
  } = useForm<CompanyBlockUnblockValidationSchema>({
    resolver: zodResolver(companyBlockUnblockSchema),
  });

  return (
    <>
      {!isDisabled && (
        <div className={twMerge("flex w-full justify-between items-center bg-white-100 rounded-lg p-2", isBlocked && 'bg-[#FCCDCA33]')}>
          <div className="flex items-center gap-x-2">
            <InfoIcon strokeWidth={1} className="text-yellow-600" />

            <div>
              <p className="font-bold text-[10px] leading-3">
                {!isBlocked ? (
                  'Bloqueio da empresa impede todas transações.'
                ) : (
                  'Desbloqueie a empresa para permitir as transações.'
                )}
              </p>

              <p className="font-light text-[10px] italic leading-3">
                {!isBlocked ? (
                  'Caso a empresa seja bloqueada, nenhuma transação poderá ser feita pela empresa.'
                ) : (
                  'Se a empresa for desbloqueada, transações poderão ser feitas.'
                )}
              </p>
            </div>
          </div>

          {!isBlocked ? (
            <Modal
              Trigger={(
                <Button operationKlassToCheckPermission="Admin::Client::Companies::Block" type="button" variant="yellow-black">
                  Bloquear empresa
                </Button>
              )}
              title="Você tem certeza?"
              subtitle="Caso a empresa seja bloqueada, nenhuma transação poderá ser feita pela empresa."
              subtitleClassName="mt-1 text-white-300"
              showClose={false}
              useCancellableFooter
              isOpen={blockModalIsOpen}
              setIsOpen={setBlockModalIsOpen}
              onConfirm={blockFormHandleSubmit((data) => blockCompany(data))}
              body={(
                <form className="flex flex-col gap-4">
                  <TextField
                    className="text-white-800 opacity-80"
                    label="Motivo"
                    {...blockFormRegister('reason')}
                    errorMessage={blockFormErrors.reason?.message || undefined}
                  />
                </form>
              )}
            />
          ) : (
            <Modal
              Trigger={(
                <Button operationKlassToCheckPermission="Admin::Client::Companies::Unblock" type="button">
                  Desbloquear empresa
                </Button>
              )}
              title="Você tem certeza?"
              subtitle="Se a empresa for desbloqueada, transações poderão ser feitas."
              subtitleClassName="mt-1 text-white-300"
              showClose={false}
              useCancellableFooter
              isOpen={unblockModalIsOpen}
              setIsOpen={setUnblockModalIsOpen}
              onConfirm={unblockFormHandleSubmit((data) => unblockCompany(data))}
              body={(
                <form className="flex flex-col gap-4">
                  <TextField
                    className="text-white-800 opacity-80"
                    label="Motivo"
                    {...unblockFormRegister('reason')}
                    errorMessage={unblockFormErrors.reason?.message || undefined}
                  />
                </form>
              )}
            />
          )}
        </div>
      )}

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </>
  );
};

export default BlockUnblockCompany;
